import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { formatDateTimeIT } from "../../lib/labels";
import { toast } from "sonner";
import { Plus, CalendarClock } from "lucide-react";

const STAGES = ["nuovo_contatto", "da_contattare", "contattato", "qualificato", "demo_fissata", "demo_effettuata", "offerta_inviata", "negoziazione", "accettata", "vinta", "persa", "sospesa"];
const ACT_TYPES = ["telefonata", "visita", "demo", "messaggio", "richiamo", "interna"];

export default function AgentDeals() {
  const [deals, setDeals] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [sel, setSel] = useState(null);
  const [acts, setActs] = useState([]);
  const [agenda, setAgenda] = useState({ overdue: [], upcoming: [] });
  const [title, setTitle] = useState("");
  const [act, setAct] = useState({ type: "telefonata", notes: "", due_at: "", next_step: "" });

  const load = async () => {
    try {
      const [d, ag] = await Promise.all([http.get("/crm/me/deals"), http.get("/crm/me/agenda")]);
      setDeals(d.data); setAgenda(ag.data); setBlocked(false);
    } catch (e) { if (e?.response?.status === 403) { setBlocked(true); setDeals([]); } else toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const openDeal = async (d) => { setSel(d); const { data } = await http.get(`/crm/me/deals/${d.id}/activities`); setActs(data); };

  const createDeal = async (e) => {
    e.preventDefault(); if (!title) return;
    try { await http.post("/crm/me/deals", { title }); setTitle(""); await load(); toast.success("Trattativa creata."); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const changeStage = async (stage) => {
    let loss_reason = null;
    if (["persa", "sospesa"].includes(stage)) { loss_reason = window.prompt("Motivo?") || ""; if (!loss_reason) return; }
    try { await http.put(`/crm/me/deals/${sel.id}/stage`, { stage, loss_reason }); await load(); setSel({ ...sel, stage }); toast.success("Stato aggiornato."); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    try {
      await http.post(`/crm/me/deals/${sel.id}/activities`, { ...act, due_at: act.due_at ? new Date(act.due_at).toISOString() : null });
      setAct({ type: "telefonata", notes: "", due_at: "", next_step: "" });
      await openDeal(sel); await load(); toast.success("Attività registrata.");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const markDone = async (aid) => { try { await http.post(`/crm/me/activities/${aid}/done`); await load(); if (sel) await openDeal(sel); } catch (e) { toast.error(formatErr(e)); } };

  if (blocked) return <div data-testid="agent-deals-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center"><h1 className="font-serif text-2xl mb-2">Trattative</h1><p className="text-sm text-neutral-600">Disponibili dopo l'attivazione del tuo account.</p></div>;
  if (deals === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  return (
    <div data-testid="agent-deals-page" className="max-w-6xl mx-auto">
      <h1 className="font-serif text-3xl mb-5">Trattative e agenda</h1>

      {(agenda.overdue.length > 0 || agenda.upcoming.length > 0) && (
        <div className="card-clean p-4 mb-5" data-testid="agent-agenda">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><CalendarClock className="w-4 h-4" /> Agenda</div>
          {agenda.overdue.map((a) => <AgendaRow key={a.id} a={a} overdue onDone={markDone} />)}
          {agenda.upcoming.map((a) => <AgendaRow key={a.id} a={a} onDone={markDone} />)}
        </div>
      )}

      <form onSubmit={createDeal} className="flex gap-2 mb-5">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo nuova trattativa" data-testid="agent-deal-title" className="flex-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }} />
        <button type="submit" data-testid="agent-deal-create" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-dark)" }}><Plus className="w-4 h-4" /> Crea</button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-clean overflow-hidden" data-testid="agent-deals-list">
          {deals.length === 0 ? <div className="p-6 text-sm text-neutral-500">Nessuna trattativa.</div> : deals.map((d) => (
            <button key={d.id} onClick={() => openDeal(d)} data-testid={`agent-deal-row-${d.id}`} className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-neutral-50 ${sel?.id === d.id ? "bg-neutral-50" : ""}`} style={{ borderColor: "var(--border-silver)" }}>
              <div className="font-medium text-sm">{d.title}</div>
              <div className="text-[11px] text-neutral-500">{d.stage}{d.loss_reason ? ` — ${d.loss_reason}` : ""}</div>
            </button>
          ))}
        </div>

        <div className="card-clean p-5" data-testid="agent-deal-detail">
          {!sel ? <p className="text-sm text-neutral-500">Seleziona una trattativa.</p> : (
            <>
              <div className="font-serif text-xl mb-2">{sel.title}</div>
              <label className="block mb-4 text-sm">
                <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Stato pipeline</span>
                <select value={sel.stage} onChange={(e) => changeStage(e.target.value)} data-testid="agent-deal-stage" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}>
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>

              <form onSubmit={addActivity} className="border-t pt-3 mb-3 text-sm space-y-2" style={{ borderColor: "var(--border-silver)" }} data-testid="agent-activity-form">
                <div className="grid grid-cols-2 gap-2">
                  <select value={act.type} onChange={(e) => setAct({ ...act, type: e.target.value })} data-testid="agent-activity-type" className="px-2 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}>
                    {ACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input type="datetime-local" value={act.due_at} onChange={(e) => setAct({ ...act, due_at: e.target.value })} data-testid="agent-activity-due" className="px-2 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
                </div>
                <input value={act.notes} onChange={(e) => setAct({ ...act, notes: e.target.value })} placeholder="Note / esito" data-testid="agent-activity-notes" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
                <input value={act.next_step} onChange={(e) => setAct({ ...act, next_step: e.target.value })} placeholder="Prossimo passo" data-testid="agent-activity-next" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
                <button type="submit" data-testid="agent-activity-save" className="px-3 py-2 rounded-md text-white text-xs" style={{ background: "var(--brand-primary)" }}>Registra attività</button>
              </form>

              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Storico attività</div>
              {acts.length === 0 ? <p className="text-xs text-neutral-400">Nessuna attività.</p> : acts.map((a) => (
                <div key={a.id} className="text-xs py-2 border-b last:border-0" style={{ borderColor: "var(--border-silver)" }}>
                  <b className="capitalize">{a.type}</b> {a.notes && `— ${a.notes}`} {a.done ? <span className="text-green-700">✓</span> : <button onClick={() => markDone(a.id)} className="underline ml-1">segna fatto</button>}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AgendaRow({ a, overdue, onDone }) {
  return (
    <div className="flex items-center justify-between text-xs py-1" data-testid={`agenda-row-${a.id}`}>
      <span className={overdue ? "text-rose-700" : ""}>
        <b className="capitalize">{a.type}</b> {a.notes ? `— ${a.notes}` : ""} {a.due_at && `· ${formatDateTimeIT(a.due_at)}`} {overdue && "(scaduta)"}
      </span>
      <button onClick={() => onDone(a.id)} className="underline">fatto</button>
    </div>
  );
}
